import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  Line,
  ComposedChart,
} from 'recharts';
import CustomTooltip from './components/customTooltip/CustomTooltip';
import {
  LOC_VS_PR_CYCLE_TIME_CHARTS,
  splitTime,
  WORK_BREAKDOWN_CHARTS,
} from '../shared/utils';
import { useCallback, useEffect, useState } from 'react';
import MetricSidebar from './components/MetricSidebar';
import moment from 'moment';
import CustomLegend from '../customLegend';
import { DateRangeSelection } from '../shared/typings';

export interface StackedBarChartDataItem {
  loc: string;
  pr_merge_time: number;
  pr_cycle_time: number;
  count: number;
  pr_pickup_time: number;
  startDate?: string;
  endDate?: string;
  name?: string;
  [key: string]: string | number | undefined;
}

export interface StackedBarChartKey {
  key: string;
  name: string;
  color: string;
  chartType: string;
}

interface StackedBarChartRechartsProps {
  data: StackedBarChartDataItem[];
  keys?: StackedBarChartKey[];
  chartMetadata?: any;
  hideBtn?: boolean;
  repos: string[];
  selectedTeam: string;
  selected?: DateRangeSelection;
  slant?: boolean;
  granularSlant?: boolean;
}

//This component is used to show the stacked bar chart for LOC vs PR Cycle Time, Work Breakdown
export const StackedBarChartRecharts = ({
  data,
  keys,
  chartMetadata,
  hideBtn,
  repos,
  selectedTeam,
  selected,
  slant,
  granularSlant,
}: StackedBarChartRechartsProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [clickedData, setClickedData] = useState<any>(null);
  const [currentChartMeta, setCurrentChartMeta] = useState<any>(null);
  const [barProps, setBarProps] = useState<any>({});

  useEffect(() => {
    setBarProps(
      keys?.reduce(
        (a: any, { key }: StackedBarChartKey) => {
          a[key] = true;
          return a;
        },
        {
          hover: null,
        }
      )
    );
  }, [keys]);
  const handleModalClose = useCallback(() => {
    setIsOpen(false);
  }, [isOpen]);

  const handleModalOpen = useCallback(() => {
    setIsOpen(true);
  }, [isOpen]);

  return (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{
            top: 20,
            left: 20,
            bottom: 30,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey={chartMetadata.xAxis}
            axisLine={false}
            tickLine={false}
            label={{
              value: chartMetadata.xlabel,
              position: 'insideBottom',
              dy: 10,
              fontSize: 11,
              fontWeight: 400,
            }}
            style={{
              fontSize: 7.5,
              fontWeight: 600,
              cursor: slant ? 'pointer' : undefined,
            }}
            height={granularSlant || slant ? 55 : undefined}
            angle={granularSlant || slant ? -60 : undefined}
            interval={granularSlant || slant ? 0 : undefined}
            textAnchor={granularSlant || slant ? 'end' : 'middle'}
          />
          <YAxis
            yAxisId="left"
            label={{
              value: chartMetadata.ylabel,
              angle: -90,
              position: 'insideLeft',
              fontSize: 11,
              fontWeight: 400,
              dx: -15,
            }}
            axisLine={false}
            tickLine={false}
            style={{ fontSize: 11, fontWeight: 600 }}
            tickFormatter={(value) => {
              if (value >= 1000000) {
                return `${(value / 1000000).toFixed(1)}M`;
              } else if (value >= 1000) {
                return `${(value / 1000).toFixed(1)}K`;
              } else {
                return value;
              }
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            label={{
              value: chartMetadata.zlabel,
              angle: 90,
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 400,
              dx: 40,
            }}
            axisLine={false}
            tickLine={false}
            width={100}
            tickFormatter={(e) => splitTime(e / 24)}
            style={{
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          />
          <Tooltip
            content={
              <CustomTooltip
                chartKey={chartMetadata.chartKey}
                handleModalOpen={handleModalOpen}
                setClickedData={setClickedData}
                unit={chartMetadata.zlabel}
                onClickAction={() => {
                  setCurrentChartMeta(chartMetadata);
                }}
                hideBtn={hideBtn}
              />
            }
            wrapperStyle={{ outline: 'none' }}
            position={{ y: -10 }}
            allowEscapeViewBox={{ x: false, y: false }}
          />
          <Legend
            verticalAlign="middle"
            layout="vertical"
            iconType="circle"
            iconSize={11}
            align="right"
            content={
              <CustomLegend
                keys={keys}
                barProps={barProps}
                setBarProps={setBarProps}
              />
            }
            wrapperStyle={{
              right: -8,
              fontSize: 11.5,
              top: 0,
            }}
          />

          {keys?.map((el) =>
            el.chartType === 'line_chart' ? (
              <Line
                key={el.key}
                yAxisId="right"
                dataKey={el.key}
                name={el.name}
                stroke={el.color}
                strokeWidth={2}
                opacity={barProps.hover && barProps.hover !== el.key ? 0.25 : 1}
                hide={barProps[el.key as string] === false}
                dot={{
                  fill: el.color,
                  stroke: el.color,
                  strokeWidth: 3,
                  r: 2,
                }}
              />
            ) : el.chartType === 'bar_chart' ? (
              <Bar
                key={el.key}
                yAxisId="left"
                dataKey={el.key}
                name={el.name}
                barSize={20}
                opacity={barProps.hover && barProps.hover !== el.key ? 0.25 : 1}
                hide={barProps[el.key as string] === false}
                fill={el.color}
              />
            ) : el.chartType === 'stacked_bar_chart' ? (
              <Bar
                key={el.key}
                dataKey={el.key}
                yAxisId={
                  chartMetadata.chartKey === 'WORK_BREAKDOWN' ? 'left' : 'right'
                }
                name={el.name}
                barSize={20}
                fill={el.color}
                stackId="a"
                opacity={barProps.hover && barProps.hover !== el.key ? 0.25 : 1}
                hide={barProps[el.key as string] === false}
                style={{
                  cursor:
                    chartMetadata.chartKey === 'WORK_BREAKDOWN'
                      ? 'pointer'
                      : 'default',
                }}
                onClick={(event: any, payload: any) => {
                  if (chartMetadata.chartKey === 'WORK_BREAKDOWN') {
                    handleModalOpen();
                    setCurrentChartMeta(chartMetadata);
                    setClickedData({
                      payload: {
                        startDate: event.startDate,
                        endDate: event.endDate,
                      },
                      name: event.name,
                    });
                  }
                }}
              />
            ) : (
              <></>
            )
          )}
        </ComposedChart>
      </ResponsiveContainer>
      {[...LOC_VS_PR_CYCLE_TIME_CHARTS, ...WORK_BREAKDOWN_CHARTS].includes(
        currentChartMeta?.chartKey
      ) &&
        isOpen && (
          <MetricSidebar
            openDrawer={isOpen}
            handleDrawerClose={handleModalClose}
            heading={chartMetadata.chartTitle}
            subHeading={
              chartMetadata.chartKey === 'WORK_BREAKDOWN'
                ? `${
                    Array.isArray(clickedData)
                      ? moment(clickedData[0]?.payload?.startDate).format(
                          'DD MMM'
                        )
                      : moment(clickedData?.payload?.startDate).format('DD MMM')
                  } - ${
                    Array.isArray(clickedData)
                      ? moment(clickedData[0]?.payload?.endDate).format(
                          'DD MMM'
                        )
                      : moment(clickedData?.payload?.endDate).format('DD MMM')
                  }`
                : `${moment(selected?.startDate).format('DD MMM')} - ${moment(
                    selected?.endDate
                  ).format('DD MMM')}`
            }
            payload={
              chartMetadata.chartKey === 'WORK_BREAKDOWN'
                ? clickedData
                : {
                    ...clickedData,

                    payload: {
                      startDate: selected?.startDate,
                      endDate: selected?.endDate,
                    },
                  }
            }
            currentChartMeta={currentChartMeta}
            filterBy={
              chartMetadata.chartKey === 'WORK_BREAKDOWN'
                ? {
                    teamId: selectedTeam,
                    sprintId: selected?.sprintId,
                    projectsFilter: repos,
                  }
                : {
                    teamId: selectedTeam,
                    sprintId: selected?.sprintId,
                    lowerBoundIncl: clickedData[0]?.payload?.loc.includes('+')
                      ? Number(clickedData[0]?.payload?.loc?.split('+')[0])
                      : clickedData[0]?.payload?.loc?.split('-')[0] &&
                        Number(clickedData[0]?.payload?.loc?.split('-')[0]),
                    upperBound: clickedData[0]?.payload?.loc.includes('+')
                      ? null
                      : clickedData[0]?.payload?.loc?.split('-')[1] &&
                        Number(clickedData[0]?.payload?.loc?.split('-')[1]),
                    prViewFilters: {
                      prSize: clickedData[0]?.payload?.loc.includes('+')
                        ? `${clickedData[0]?.payload?.loc?.split('+')[0]}-${
                            clickedData[0]?.payload?.loc?.split('+')[1]
                          }`
                        : clickedData[0]?.payload?.loc?.split('-')[0] &&
                          clickedData[0]?.payload?.loc?.split('-')[1]
                        ? `${clickedData[0]?.payload?.loc?.split('-')[0]}-${
                            clickedData[0]?.payload?.loc?.split('-')[1]
                          }`
                        : undefined,
                    },
                  }
            }
            selectedTeam={selectedTeam}
          />
        )}
    </>
  );
};
